import type { KyInstance } from "ky";

/**
 * Frontend-only display labels for known field types, kept separate from `TaxonomyFields`
 * (the actual `/taxonomy/fields` response shape) on purpose: the backend's field types are
 * DB-driven and open-ended, so the API contract stays a plain `string[]` rather than this
 * literal union. Consumers should fall back to the raw field type when a key is missing here,
 * since the backend can introduce a new one before this map is updated.
 */
export const TAXONOMY_FIELD_LABELS = {
  title: "Title",
  registeredBody: "Registered Body",
  psychologyEndorsements: "Psychology Endorsements",
  ageGroups: "Age Groups",
  clientTypes: "Client Types",
  fundingPathways: "Funding Pathways",
  feeArrangements: "Fee Arrangements",
  thirdPartySchemes: "Third Party Schemes",
  culturalBackground: "Cultural Background",
  languages: "Languages",
  therapeuticApproaches: "Therapeutic Approaches",
  focusAreas: "Area of Focus",
  otherFocusAreas: "Other Areas of Focus",
  inquirySessionType: "Inquiry Session Type",
} as const;

export type TaxonomyFieldType = keyof typeof TAXONOMY_FIELD_LABELS;

export type TaxonomyFields = string[];

export interface TaxonomySchemaMapping {
  name?: string;
  codes?: string[];
  sameAs?: string[];
  credentialCategory?: string;
  minAge?: number;
  maxAge?: number;
  exclude?: boolean;
}

export interface TaxonomyOption {
  slug: string;
  label: string;
  children?: TaxonomyOption[];
  schema?: TaxonomySchemaMapping;
}

export interface Taxonomy {
  fieldType: string;
  options: TaxonomyOption[];
}

export type FieldValueStatus = "active" | "archived";

export interface AdminTaxonomyOption {
  slug: string;
  label: string;
  status: FieldValueStatus;
  children?: AdminTaxonomyOption[];
}

export interface CreateTaxonomyOptionInput {
  label: string;
  parentSlug?: string;
}

export class TaxonomyApi {
  constructor(private readonly http: KyInstance) { }

  fields = (): Promise<TaxonomyFields> =>
    this.http.get("taxonomy/fields").json<TaxonomyFields>();

  list = (): Promise<Taxonomy[]> =>
    this.http.get("taxonomy").json<Taxonomy[]>();

  listOne = (fieldType: string): Promise<TaxonomyOption[]> =>
    this.http.get(`taxonomy/${encodeURIComponent(fieldType)}`).json<TaxonomyOption[]>();
}

export class AdminTaxonomyApi {
  constructor(private readonly http: KyInstance) { }

  list = (fieldType: string): Promise<AdminTaxonomyOption[]> =>
    this.http.get(`admin/taxonomy/${encodeURIComponent(fieldType)}`).json<AdminTaxonomyOption[]>();

  create = (fieldType: string, input: CreateTaxonomyOptionInput): Promise<AdminTaxonomyOption> =>
    this.http
      .post(`admin/taxonomy/${encodeURIComponent(fieldType)}`, { json: input })
      .json<AdminTaxonomyOption>();

  archive = (fieldType: string, slug: string): Promise<AdminTaxonomyOption> =>
    this.http
      .post(`admin/taxonomy/${encodeURIComponent(fieldType)}/${encodeURIComponent(slug)}/archive`)
      .json<AdminTaxonomyOption>();
}
